import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import {
  Briefcase, DollarSign, Users, FileText, ArrowLeft, Save, Send, Loader2
} from 'lucide-react';

export default function CreateJob() {
  const { activeUserId } = useAuth();
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    role: '',
    description: '',
    salary_min: '',
    salary_max: '',
    currency: 'AED',
    vacancies: '1',
    requirements: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (publish: boolean) => {
    if (!formData.role.trim()) {
      alert('Please enter a job role');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from('jobs').insert({
        campaign_id: campaignId,
        role: formData.role,
        description: formData.description,
        salary_min: parseInt(formData.salary_min) || null,
        salary_max: parseInt(formData.salary_max) || null,
        currency: formData.currency,
        vacancies: parseInt(formData.vacancies) || 1,
        requirements: formData.requirements,
        status: publish ? 'active' : 'draft',
      });
      if (error) throw error;
      navigate(`/app/campaigns/${campaignId}`);
    } catch (err) {
      console.error('Error creating job:', err);
      alert('Failed to create job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-2.5 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 focus:bg-white transition-all placeholder:text-slate-400 text-[13px] font-medium";

  return (
    <div className="max-w-3xl mx-auto pb-12 animate-in fade-in" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <button
        onClick={() => navigate(`/app/campaigns/${campaignId}`)}
        className="flex items-center gap-2 text-slate-400 hover:text-slate-700 mb-6 text-[13px] font-medium"
      >
        <ArrowLeft size={14} /> Back to Campaign
      </button>

      <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-1">Add Job Role</h1>
      <p className="text-slate-400 text-[15px] mb-8">Create a new position under this campaign</p>

      <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm space-y-5">
        <div>
          <label className="block text-[12px] font-semibold text-slate-500 mb-1.5 flex items-center gap-2"><Briefcase size={16} /> Job Role / Title</label>
          <input required name="role" value={formData.role} onChange={handleChange} placeholder="e.g. General Cleaner" className={inputClass} />
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-slate-500 mb-1.5 flex items-center gap-2"><FileText size={16} /> Description</label>
          <textarea name="description" value={formData.description} onChange={handleChange} rows={3} placeholder="Job responsibilities and details..." className={`${inputClass} resize-none`} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-[12px] font-semibold text-slate-500 mb-1.5"><DollarSign size={16} className="inline" /> Min Salary</label>
            <input type="number" name="salary_min" value={formData.salary_min} onChange={handleChange} placeholder="1500" className={inputClass} />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Max Salary</label>
            <input type="number" name="salary_max" value={formData.salary_max} onChange={handleChange} placeholder="2000" className={inputClass} />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Currency</label>
            <select name="currency" value={formData.currency} onChange={handleChange} className={inputClass}>
              <option value="AED">AED</option>
              <option value="SAR">SAR</option>
              <option value="QAR">QAR</option>
              <option value="INR">INR</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-[12px] font-semibold text-slate-500 mb-1.5 flex items-center gap-2"><Users size={16} /> Vacancies</label>
            <input type="number" name="vacancies" value={formData.vacancies} onChange={handleChange} placeholder="1" className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-2">Requirements</label>
            <input name="requirements" value={formData.requirements} onChange={handleChange} placeholder="e.g. 2 years experience" className={inputClass} />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-6">
        <button onClick={() => navigate(`/app/campaigns/${campaignId}`)} className="px-5 py-2.5 text-slate-400 text-[13px] font-semibold hover:text-slate-700 transition-colors">Cancel</button>
        <button
          onClick={() => handleSave(false)}
          disabled={loading}
          className="flex items-center gap-2 bg-white text-slate-800 border border-slate-200 px-5 py-2.5 rounded-xl text-[13px] font-semibold hover:bg-slate-50 transition-all disabled:opacity-50"
        >
          <Save size={15} /> Save Draft
        </button>
        <button
          onClick={() => handleSave(true)}
          disabled={loading}
          className="flex items-center gap-2 bg-slate-950 text-white px-5 py-2.5 rounded-xl text-[13px] font-semibold hover:bg-slate-800 transition-all disabled:opacity-50"
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : <><Send size={15} /> Publish Job</>}
        </button>
      </div>
    </div>
  );
}
